import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IManagedDbColumnFilter,
  IManagedDbRow,
  IManagedDbRowsResponse,
  TableEditorService
} from "../../services/table-editor-service";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../ui-kit/kuika-popover/kuika-popover";
import { buildColumnFilters, IColumnFilterEntry, IColumnFilterMap } from "./column-filters";
import FilterCell from "./filter-cell";

export interface IFkPickerProps {
  refTableName: string;
  refTableSchema?: string;
  // The actual column on the referenced table this FK points at —
  // not necessarily the PK. For `Orders.CustomerEmail → Customers.Email`
  // the picker has to emit row["Email"], not row["Id"]. When the
  // server-side FK introspection couldn't resolve it (very old
  // payloads) we fall back to the referenced table's first PK.
  refColumnName?: string;
  // Source column display name — surfaced in the popover title so the user
  // knows which cell they're filling.
  sourceColumnName: string;
  currentValue?: unknown;
  onPick: (value: unknown) => void;
  // The button (or other affordance) the popover anchors to.
  children: React.ReactNode;
}

const PAGE_SIZE: number = 25;

// Sized to fit ~8 rows × ~5 columns comfortably and matches the Cell
// Expander's editor surface in width so the two modals don't feel like
// they came from different feature teams. Users can drag the bottom-
// right corner (CSS resize:both) to make it bigger; the new size is
// persisted per refTable so the next open recalls their preference.
const DEFAULT_PICKER_WIDTH: number = 720;
const DEFAULT_PICKER_HEIGHT: number = 440;
const MIN_PICKER_WIDTH: number = 480;
const MIN_PICKER_HEIGHT: number = 320;
const PICKER_SIZE_STORAGE_PREFIX: string = "kuika-table-editor-fk-picker-size-v1";

interface IPickerSize {
  width: number;
  height: number;
}

const pickerSizeKey = (schema: string | undefined, table: string): string =>
  `${PICKER_SIZE_STORAGE_PREFIX}:${schema ?? "_"}.${table}`;

const readPersistedPickerSize = (schema: string | undefined, table: string): IPickerSize | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }
    const raw: string | null = window.localStorage.getItem(pickerSizeKey(schema, table));
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      typeof (parsed as IPickerSize).width !== "number" ||
      typeof (parsed as IPickerSize).height !== "number"
    ) {
      return null;
    }
    const width: number = (parsed as IPickerSize).width;
    const height: number = (parsed as IPickerSize).height;
    if (width < MIN_PICKER_WIDTH || height < MIN_PICKER_HEIGHT) {
      return null;
    }
    return { width, height };
  } catch {
    return null;
  }
};

const writePersistedPickerSize = (schema: string | undefined, table: string, size: IPickerSize): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(pickerSizeKey(schema, table), JSON.stringify(size));
  } catch {
    // Quota exceeded, private browsing, etc. — silently drop. Users
    // get the live size; they just lose persistence.
  }
};

const FkPicker: React.FC<IFkPickerProps> = (props: IFkPickerProps): JSX.Element => {
  const { refTableName, refTableSchema, refColumnName, sourceColumnName, currentValue, onPick, children } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Per-column filter map matching the main grid's contract — same
  // FilterCell component renders each entry so the picker reads as a
  // miniature of the main editor. Cleared on (re)open.
  const [columnFilters, setColumnFilters] = useState<IColumnFilterMap>({});
  // Debounced wire-format filters. The user types, columnFilters
  // updates immediately for snappy UI, then a 220 ms debounce promotes
  // the value to debouncedFilters which actually drives the fetch.
  const [debouncedFilters, setDebouncedFilters] = useState<IManagedDbColumnFilter[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [columns, setColumns] = useState<IDatabaseColumnModel[]>([]);
  const [pkColumns, setPkColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<IManagedDbRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef<number>(0);

  // Resizable container — width / height are kept in state so we can
  // persist them on pointer-up. ResizeObserver tracks the live CSS-
  // driven dimensions (set by the user dragging the resize handle).
  const resizableRef = useRef<HTMLDivElement | null>(null);
  const [pickerSize, setPickerSize] = useState<IPickerSize>(
    (): IPickerSize =>
      readPersistedPickerSize(refTableSchema, refTableName) ?? {
        width: DEFAULT_PICKER_WIDTH,
        height: DEFAULT_PICKER_HEIGHT
      }
  );

  // Reset on (re)open AND on close. The grid renders one FkPicker per FK
  // cell — without clearing on close, each closed picker keeps its last
  // fetched page in memory until the editor unmounts. On a 50-row page
  // with five FK columns that's 250 retained snapshots of someone else's
  // table. Bump the seq ref too so any in-flight request resolves
  // against the new sequence and discards.
  useEffect((): void => {
    if (!isOpen) {
      // Closed → forget what we fetched. Bump the seq so a late response
      // for the previous open won't drop its payload into the next one.
      requestSeqRef.current = requestSeqRef.current + 1;
      setColumns([]);
      setPkColumns([]);
      setRows([]);
      setTotalCount(0);
      setIsLoading(false);
      setError(null);
      setColumnFilters({});
      setDebouncedFilters([]);
      setPageNumber(1);
      return;
    }
    setColumnFilters({});
    setDebouncedFilters([]);
    setPageNumber(1);
    setError(null);
    // Recall the user's last sizing preference for this referenced
    // table. Different FK targets have different column counts and
    // value widths, so per-table persistence (vs. one global picker
    // size) gives every referenced table the right room.
    setPickerSize(
      readPersistedPickerSize(refTableSchema, refTableName) ?? {
        width: DEFAULT_PICKER_WIDTH,
        height: DEFAULT_PICKER_HEIGHT
      }
    );
  }, [isOpen, refTableName, refTableSchema]);

  // Debounce columnFilters → debouncedFilters with a 220 ms window so
  // we don't fire a request per keystroke.
  useEffect((): (() => void) | undefined => {
    if (!isOpen) {
      return undefined;
    }
    const next: IManagedDbColumnFilter[] = buildColumnFilters(columnFilters);
    const t: number = window.setTimeout((): void => setDebouncedFilters(next), 220);
    return (): void => window.clearTimeout(t);
  }, [columnFilters, isOpen]);

  // Reset to page 1 when the active filter changes.
  useEffect((): void => {
    setPageNumber(1);
  }, [debouncedFilters]);

  useEffect((): (() => void) | undefined => {
    if (!isOpen) {
      return undefined;
    }
    // AbortController per fetch — abort on effect cleanup (filter / page /
    // refTable change OR close). Pre-fix we used a sequence guard that
    // ignored late responses but the browser still downloaded + parsed
    // them. Aborting also drops the network leg.
    const controller: AbortController = new AbortController();
    const mySeq: number = requestSeqRef.current + 1;
    requestSeqRef.current = mySeq;
    setIsLoading(true);
    setError(null);

    // K-prefixed system tables always live in dbo. If the FK metadata didn't
    // include refTableSchema (older payloads), fall back to dbo for those so
    // the picker actually finds the referenced table.
    const effectiveSchema: string | undefined = refTableSchema ?? (refTableName.startsWith("K") ? "dbo" : undefined);

    (async (): Promise<void> => {
      try {
        const response = await TableEditorService.QueryRows(
          refTableName,
          {
            tableSchema: effectiveSchema,
            pageNumber,
            pageSize: PAGE_SIZE,
            filters: debouncedFilters
          },
          controller.signal
        );
        if (controller.signal.aborted || requestSeqRef.current !== mySeq) {
          return;
        }
        const payload: IManagedDbRowsResponse = response.data;
        setColumns(payload.columns ?? []);
        setPkColumns(payload.primaryKeyColumns ?? []);
        setRows(payload.rows ?? []);
        setTotalCount(payload.totalCount ?? 0);
      } catch (e: unknown) {
        if (axios.isCancel(e) || controller.signal.aborted) {
          return;
        }
        if (requestSeqRef.current === mySeq) {
          setError(e instanceof Error ? e.message : "Failed to load");
        }
      } finally {
        if (requestSeqRef.current === mySeq) {
          setIsLoading(false);
        }
      }
    })().catch((): void => {});

    return (): void => {
      controller.abort();
    };
  }, [isOpen, refTableName, refTableSchema, debouncedFilters, pageNumber]);

  // Track the user's drag-resize via ResizeObserver so we can persist
  // the new dimensions when they release. ResizeObserver fires per
  // animation frame during the drag — debounce write-to-storage to the
  // final size (after a 200 ms quiet period).
  useEffect((): (() => void) | undefined => {
    if (!isOpen || resizableRef.current === null) {
      return undefined;
    }
    const node: HTMLDivElement = resizableRef.current;
    let writeTimeout: number | null = null;
    const observer: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const entry: ResizeObserverEntry | undefined = entries[0];
      if (entry === undefined) {
        return;
      }
      const width: number = Math.round(entry.contentRect.width);
      const height: number = Math.round(entry.contentRect.height);
      if (width < MIN_PICKER_WIDTH || height < MIN_PICKER_HEIGHT) {
        return;
      }
      if (writeTimeout !== null) {
        window.clearTimeout(writeTimeout);
      }
      writeTimeout = window.setTimeout((): void => {
        writePersistedPickerSize(refTableSchema, refTableName, { width, height });
      }, 200);
    });
    observer.observe(node);
    return (): void => {
      observer.disconnect();
      if (writeTimeout !== null) {
        window.clearTimeout(writeTimeout);
      }
    };
  }, [isOpen, refTableSchema, refTableName]);

  // Column whose value we emit on row click. Prefers the FK's actual
  // referenced column (which may be a unique key, not the PK), falls
  // back to the referenced table's first PK only when the server
  // couldn't resolve a refColumnName. The fallback covers legacy
  // payloads — current backend always populates RefColumnName for
  // single-column FKs.
  const pickColumn: string | undefined = refColumnName ?? pkColumns[0];
  const totalPages: number = totalCount > 0 ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

  const previewColumns: IDatabaseColumnModel[] = useMemo((): IDatabaseColumnModel[] => {
    // DataGrip-style ordering: PK → required → nullable, declared order
    // within each group. Picker rows scroll horizontally — no column
    // trimming — so the user sees identity first, then the meaningful
    // signals, then the optional tail.
    const pkSet: Set<string> = new Set(pkColumns);
    const pkFirst: IDatabaseColumnModel[] = pkColumns
      .map((name: string): IDatabaseColumnModel | undefined =>
        columns.find((c: IDatabaseColumnModel): boolean => c.name === name)
      )
      .filter((c: IDatabaseColumnModel | undefined): c is IDatabaseColumnModel => c !== undefined);
    const rest: IDatabaseColumnModel[] = columns.filter((c: IDatabaseColumnModel): boolean => !pkSet.has(c.name));
    const required: IDatabaseColumnModel[] = rest.filter((c: IDatabaseColumnModel): boolean => c.nullable === false);
    const nullable: IDatabaseColumnModel[] = rest.filter((c: IDatabaseColumnModel): boolean => c.nullable !== false);
    return [...pkFirst, ...required, ...nullable];
  }, [columns, pkColumns]);

  const activeFilterCount: number = useMemo((): number => buildColumnFilters(columnFilters).length, [columnFilters]);

  // Mirror the Expand Cell modal's header: kicker + title + meta pills.
  // The popover's `title` prop accepts ReactNode so we pass the styled
  // span directly. Pills surface the cell being filled + the row count
  // + the active filter count so the at-a-glance state is always
  // visible in the chrome.
  const headerNode: JSX.Element = (
    <span className="kuika-table-editor__cell-expander-header kuika-table-editor__fk-picker-header">
      <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
        Pick foreign key
      </span>
      <span className="kuika-table-editor__cell-expander-header-title">{refTableName}</span>
      <span className="kuika-table-editor__cell-expander-meta-pills">
        <span className="kuika-table-editor__cell-expander-pill">filling {sourceColumnName}</span>
        {totalCount > 0 && (
          <span className="kuika-table-editor__cell-expander-pill">{totalCount.toLocaleString("en-US")} rows</span>
        )}
        {activeFilterCount > 0 && (
          <span className="kuika-table-editor__cell-expander-pill">
            {activeFilterCount === 1 ? "1 filter" : `${activeFilterCount} filters`}
          </span>
        )}
      </span>
    </span>
  );

  const body: JSX.Element = (
    <div
      ref={resizableRef}
      className="kuika-table-editor__fk-picker"
      style={{ width: pickerSize.width, height: pickerSize.height }}
    >
      {error && <div className="kuika-table-editor__banner kuika-table-editor__banner--error">{error}</div>}

      <div className="kuika-table-editor__fk-picker-list">
        {isLoading && <div className="kuika-table-editor__loading-bar" />}
        {/* First load (no columns yet) — show a centered Loading… stub
            until the schema arrives, then the table mounts with the
            filter row + rows from the response. */}
        {columns.length === 0 ? (
          <div className="kuika-table-editor__fk-picker-empty">{isLoading ? "Loading…" : "No records match."}</div>
        ) : (
          // Real <table> so the browser establishes one consistent width per
          // column across rows. table-layout: auto sizes each column to its
          // widest cell, then the list container scrolls horizontally for
          // wide tables. We keep it mounted during reloads so the user
          // sees what they had while pagination / filter refreshes.
          <table className="kuika-table-editor__fk-picker-table">
            <thead>
              <tr>
                {previewColumns.map((col: IDatabaseColumnModel): JSX.Element => (
                  <th
                    key={col.name}
                    className={pkColumns.includes(col.name) ? "is-pk" : undefined}
                    title={col.kuikaDataType ?? ""}
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
              {/* Per-column filter row — same FilterCell as the main
                  grid so the picker reads as the main editor in
                  miniature. Filter input + operator picker per column;
                  values debounce + drive the fetch via debouncedFilters. */}
              <tr className="kuika-table-editor__fk-picker-filter-row">
                {previewColumns.map((col: IDatabaseColumnModel): JSX.Element => (
                  <th key={col.name} className="kuika-table-editor__filter-row-cell">
                    <FilterCell
                      column={col}
                      entry={columnFilters[col.name]}
                      onChange={(next: IColumnFilterEntry | null): void => {
                        setColumnFilters((prev: IColumnFilterMap): IColumnFilterMap => {
                          const updated: IColumnFilterMap = { ...prev };
                          if (next === null) {
                            delete updated[col.name];
                          } else {
                            updated[col.name] = next;
                          }
                          return updated;
                        });
                      }}
                      ariaPrefix="FK browse"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && rows.length === 0 && (
                <tr>
                  <td className="kuika-table-editor__fk-picker-empty" colSpan={previewColumns.length}>
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td className="kuika-table-editor__fk-picker-empty" colSpan={previewColumns.length}>
                    No records match.
                  </td>
                </tr>
              )}
              {rows.map((row: IManagedDbRow, idx: number): JSX.Element => {
                const pickedValue: unknown = pickColumn ? row[pickColumn] : undefined;
                const isCurrent: boolean =
                  pickColumn !== undefined &&
                  currentValue !== null &&
                  currentValue !== undefined &&
                  String(pickedValue) === String(currentValue);
                return (
                  <tr
                    key={String(pickedValue ?? idx)}
                    className={
                      "kuika-table-editor__fk-picker-row" +
                      (isCurrent ? " kuika-table-editor__fk-picker-row--current" : "") +
                      (pickedValue === null || pickedValue === undefined ? " is-disabled" : "")
                    }
                    onClick={(): void => {
                      if (pickedValue === null || pickedValue === undefined) {
                        return;
                      }
                      onPick(pickedValue);
                      setIsOpen(false);
                    }}
                  >
                    {previewColumns.map((col: IDatabaseColumnModel): JSX.Element => {
                      const v: unknown = row[col.name];
                      const isNull: boolean = v === null || v === undefined;
                      const display: string = isNull ? "NULL" : String(v);
                      return (
                        <td
                          key={col.name}
                          className={pkColumns.includes(col.name) ? "is-pk" : undefined}
                          title={display}
                        >
                          {isNull ? <span className="kuika-table-editor__fk-picker-null">NULL</span> : display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* fk-picker-list */}

      <div className="kuika-table-editor__fk-picker-footer">
        <span>
          {totalCount > 0
            ? `${(pageNumber - 1) * PAGE_SIZE + 1}–${Math.min(pageNumber * PAGE_SIZE, totalCount)} of ${totalCount}`
            : "0"}
        </span>
        <span className="kuika-table-editor__fk-picker-footer-spacer" />
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          color="neutral"
          disabled={pageNumber <= 1 || isLoading}
          onClick={(): void => setPageNumber(Math.max(1, pageNumber - 1))}
        >
          ‹
        </KuikaButton>
        <span className="kuika-table-editor__fk-picker-footer-pos">
          {pageNumber} / {totalPages}
        </span>
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          color="neutral"
          disabled={pageNumber >= totalPages || isLoading}
          onClick={(): void => setPageNumber(Math.min(totalPages, pageNumber + 1))}
        >
          ›
        </KuikaButton>
      </div>
    </div>
  );

  return (
    <KuikaPopover
      visible={isOpen}
      onVisibleChange={setIsOpen}
      title={headerNode}
      position="bottom"
      className="kuika-table-editor__fk-popover"
      content={body}
    >
      {children}
    </KuikaPopover>
  );
};

export default FkPicker;
